import type { ReactNode } from 'react';

/**
 * The template's error / coming-soon layout: an oversized duotone glyph, an optional status code
 * set large, a heading, a sentence of explanation, and the way out.
 *
 * One component for all four screens rather than four near-identical files, because the only thing
 * that actually differs between "404", "500", "under maintenance" and "coming soon" is the wording
 * and the colour — and four copies of the same centred column is how three of them end up subtly
 * out of step with the fourth. The template ships exactly that: `error404`, `error500` and
 * `comingSoon` are the same markup three times, differing in a glyph, a tone and two sentences.
 *
 * The proportions are the template's own — a `text-9xl` glyph over a `text-9xl` code — stepped down
 * on small screens, where the template's fixed size overflows a phone. The glyph carries the tone
 * bare rather than sitting on a tinted disc, which is how the template draws it and what makes the
 * 404 read as a poster rather than as an oversized toast.
 */
export type StatusTone = 'brand' | 'warning' | 'danger';

/**
 * The glyph colour. All three are theme tokens, redefined under `[data-theme="dark"]` — a
 * hand-picked hex here would be a light-mode colour sitting on a dark page.
 */
const TONES: Record<StatusTone, string> = {
  brand: 'text-primary',
  warning: 'text-warning',
  danger: 'text-danger',
};

export function StatusScreen({
  code,
  icon,
  tone = 'brand',
  title,
  message,
  actions,
  standalone = false,
}: {
  /**
   * The status printed large above the heading — `404`, `500`. Omitted where there is no status to
   * print: "coming soon" is not a failure and giving it a number would imply the server refused
   * something.
   */
  code?: string;

  /** A Phosphor duotone class pair, e.g. `ph-duotone ph-compass`. */
  icon: string;

  tone?: StatusTone;
  title: string;
  message: ReactNode;

  /** Buttons. Optional, because two of these screens have nowhere useful to send anyone. */
  actions?: ReactNode;

  /**
   * Fill the viewport instead of the shell's content column.
   *
   * Set by the screens that render *instead of* the shell — a crash caught by the error boundary,
   * or an API that cannot be reached before sign-in. Left off inside the shell, where the sidebar
   * is still working and taking it away would strand the user on the one screen that failed.
   */
  standalone?: boolean;
}) {
  const block = (
    <div className="mx-auto flex max-w-lg flex-col items-center px-4 py-12 text-center">
      {/* aria-hidden: decorative. The heading below carries the meaning. */}
      <i className={`${icon} mb-4 text-7xl sm:text-9xl ${TONES[tone]}`} aria-hidden="true" />

      {/*
        A `p`, not the template's `h1`. The template makes "404" the page heading; a screen reader
        then announces the number and never reaches the sentence that says what happened. The
        heading below is the heading, and this is the poster type above it.
      */}
      {code && (
        <p className="mb-2 text-7xl leading-none font-bold text-title sm:text-9xl">{code}</p>
      )}

      {/*
        With a code above it the title is the template's `text-xl` subheading; without one it *is*
        the display type, exactly as `comingSoon` sets it. Nothing else distinguishes the two
        layouts, so deriving it here keeps the calling screens from having to know.
      */}
      <h1
        className={
          code
            ? 'mb-2 text-xl font-semibold text-title'
            : 'mb-2 text-3xl font-bold text-title sm:text-4xl'
        }
      >
        {title}
      </h1>

      <div className="text-sm text-default">{message}</div>

      {actions && (
        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">{actions}</div>
      )}
    </div>
  );

  if (!standalone) {
    return block;
  }

  return <div className="grid min-h-screen place-items-center bg-light p-4">{block}</div>;
}
